import { CancelJoinSection } from 'app/src/features/UserJoin/components/ConfirmStep/CancelJoinSection'
import type { JoinStep, PercentageSimulationData } from 'app/src/features/UserJoin/UserJoin'
import { membershipApi } from 'app/src/services/welfareportal'
import { AttachmentList, PageWidget } from 'app/src/shared/components'
import { euroFormatterIt, getFromSessionStorage } from 'app/src/shared/helpers'
import { useTranslation, useWebAppContext } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Divider } from 'primereact/divider'
import { useCallback, useMemo, useState, type Dispatch, type ReactElement, type SetStateAction } from 'react'

export function UserJoinConfirm(
  props: Readonly<{
    formData?: PercentageSimulationData
    chosenPercentage: number
    userInfoPercentage?: number
    userStatus?: membershipApi.MembershipStatus
    overviewVisited: boolean
    setCurrentStep: (step: JoinStep) => void
    setConfirmPercentageDialogVisible: Dispatch<SetStateAction<boolean>>
  }>,
): ReactElement {
  const context = useWebAppContext()
  const { t } = useTranslation('joinFunnel', { keyPrefix: 'JoinFunnel.Confirm' })
  const tCommon = useTranslation('joinFunnel', { keyPrefix: 'JoinFunnel.Common' })

  const campaignStorage = getFromSessionStorage('selectedCampaign') ?? '{}'
  const membershipId = JSON.parse(campaignStorage)?.membershipId

  const membershipMutation = membershipApi.useMembershipJoin({
    onSuccess: () => {
      props.setCurrentStep('success')
    },
    onError: (error: membershipApi.MembershipJoinError) => {
      context.logger?.error('Error Joining Membership', { propertyName: 'percentage', error })
      props.setCurrentStep('error')
    },
  })

  const [privacyChecked, setPrivacyChecked] = useState<boolean | undefined>(false)
  const [rulesChecked, setRulesChecked] = useState<boolean | undefined>(false)

  const filteredDocuments = useMemo(
    () =>
      context.userInfo.welfareCampaign.documents
        .filter((el) => {
          if (el.type === 'privacy_policy') return true
          if (el.type === 'terms_and_conditions') return true
          return false
        })
        .map((d) => d.documentId!),
    [context.userInfo.welfareCampaign.documents],
  )

  const updateUserPercentage = useCallback(async () => {
    // if (!isEqual(props.propertyValue, values[props.propertyName])) {
    await membershipMutation.mutateAsync({
      pathParams: {
        membershipId,
      },
      body: {
        percentage: parseFloat(props.chosenPercentage.toFixed(3)),
        userId: context.userInfo.userId,
        acknowledgeDocuments: filteredDocuments,
      },
    })

    // } else props.onClose()
  }, [membershipMutation, props, context, membershipId, filteredDocuments])
  return props.userStatus ? (
    <>
      <PageWidget>
        {props.overviewVisited && (
          <Button
            link
            onClick={() => props.setCurrentStep('overview')}
            icon='pi pi-arrow-left'
            pt={{ root: { className: 'mb-2 pl-0' } }}
          >
            <span className='ml-2 font-bold'>{t('goBackBtnLabel')}</span>
          </Button>
        )}
        <h1 className='text-center text-[28px] font-semibold'>{t('title')}</h1>
        <div className='m-4 grid gap-4 text-center lg:grid-cols-2'>
          <div className='lg:border-r-2'>
            <div className=''>{t('convertSection.textBeforeFigure')}</div>
            <div className='m-4'>
              <div className='text-[28px] font-semibold'>
                {props.formData?.simulationData
                  ? ((props.chosenPercentage ?? 0) * 100).toFixed(
                      context.userInfo.welfareCampaign.configuration.joinConfig?.choiceMode === 'slider' ? 1 : 0,
                    ) +
                    '% ' +
                    t('convertSection.percentageDescription')
                  : tCommon.t('notAvailableText')}
              </div>
            </div>
            <div className='h2 m-4'>
              {props.userInfoPercentage === 0 ? (
                t('convertSection.textAfterFigure')
              ) : props.userInfoPercentage && props.userInfoPercentage > 0 ? (
                <Button
                  link
                  label={t('convertSection.modifyPercentageBtnLabel')}
                  onClick={() => props.setCurrentStep('overview')}
                />
              ) : (
                ''
              )}
            </div>
          </div>
          <div className='lg:hidden'>
            <Divider />
          </div>
          <div>
            <div>{t('additionalCreditSection.textBeforeFigure')}</div>
            <div className='m-4 text-[28px] font-semibold'>
              {props.formData?.simulationData?.integrazionePoste
                ? '+' + euroFormatterIt.format(props.formData?.simulationData?.integrazionePoste)
                : tCommon.t('notAvailableText')}
            </div>
            {props.formData?.simulationData?.integrazionePoste ? <></> : <div>{tCommon.t('notAvailableDescriptionText')}</div>}
            <div>{t('additionalCreditSection.textAfterFigure')}</div>
          </div>
        </div>
        {props.chosenPercentage === 1 ? (
          <div className='w-full rounded-lg border bg-[var(--card-background)] p-4 text-center shadow-md'>
            {t('conversionSummaryBannerText')}{' '}
            <span className='font-semibold'>
              {props.formData?.simulationData?.totaleNetto
                ? euroFormatterIt.format(props.formData?.simulationData?.totaleNetto)
                : tCommon.t('notAvailableText')}
            </span>
          </div>
        ) : (
          <div className='w-full rounded-lg border bg-[var(--card-background)] p-4 text-center shadow-md'>
            <div
              dangerouslySetInnerHTML={{
                __html: t('conversionSummaryBannerTextDetails', {
                  NETTICREDITO: props.formData?.simulationData?.conversioneInCredito
                    ? euroFormatterIt.format(
                        props.formData?.simulationData?.conversioneInCredito + props.formData.simulationData.integrazionePoste,
                      )
                    : tCommon.t('notAvailableText'),
                  NETTIBUSTAPAGA: props.formData?.simulationData?.importoBustaPaga
                    ? euroFormatterIt.format(props.formData?.simulationData?.importoBustaPaga)
                    : tCommon.t('notAvailableText'),
                }),
              }}
            ></div>
          </div>
        )}
        <div className='mt-4'>{t('rulesAndPrivacyMessage')}</div>
        <div className='mb-4'>
          <AttachmentList
            attachments={context.userInfo.welfareCampaign.documents
              .filter((el) => el.type === 'privacy_policy' || el.type === 'terms_and_conditions')
              .map((d) => ({
                id: d.documentId!,
                name: d.name!,
                resourceLink: d.url,
                type: d.type!,
              }))}
            title={'Documenti utili'}
          />
        </div>
        {props.chosenPercentage !== props.userInfoPercentage && (
          <>
            <div className='mb-4 w-full'>
              <div className='flex items-center'>
                <Checkbox
                  checked={privacyChecked ?? false}
                  inputId='privacy'
                  name='privacy'
                  onChange={(e) => {
                    setPrivacyChecked(e.checked)
                  }}
                />
                <label htmlFor='privacy' className='ml-2'>
                  {t('joinRulesCheckboxes')[0]}
                </label>
              </div>

              <div className='flex items-center'>
                <Checkbox
                  checked={rulesChecked ?? false}
                  inputId='rules'
                  name='rules'
                  onChange={(e) => {
                    setRulesChecked(e.checked)
                  }}
                />
                <label htmlFor='rules' className='ml-2'>
                  {t('joinRulesCheckboxes')[1]}
                </label>
              </div>
            </div>
            <div className='text-center'>
              <Button
                rounded
                label={t('joinBtnLabel')}
                disabled={!(rulesChecked && privacyChecked && (props.chosenPercentage === 0 || filteredDocuments.length > 0))}
                onClick={() => {
                  updateUserPercentage()
                }}
              ></Button>
            </div>
          </>
        )}
      </PageWidget>
      {props.userInfoPercentage && props.userInfoPercentage > 0 ? (
        <>
          <CancelJoinSection
            onSuccess={() => props.setCurrentStep('unjoinSuccess')}
            onError={() => props.setCurrentStep('error')}
          />
        </>
      ) : (
        <></>
      )}
    </>
  ) : (
    <></>
  )
}
